/* eslint-disable no-case-declarations */
import { cloneDeep }                                  from 'lodash-es';
import { ActionTree }                                 from 'vuex';
import { AxiosPromise, AxiosResponse }                from 'axios';
import i18n                                           from '@/i18n';
import { FilterConfiguration,
  FilterConfigurationJSON,
  State }                                                     from '@/components/chart/chart-wrapper/store/chart-wrapper.state.store';
import { RootState }                                  from '@/store/store.types';
import { Notification }                               from '@/components/notification/notification';
import { ChartDataResponse }                          from '@/components/chart/chart.types';
import { Filter,
  FILTER_TYPE,
  FilterOption,
  FilterPanelState,
  FilterStylesObject,
  Group,
  GROUP_NAME,
  RequestFilter,
  SelectedFilter,
  Tab,
  TrackedTab }                                                     from '@/components/filter-panel/filter-panel.types';
import { composeFiltersToSend,
  getAppliedTabInGivenGroup,
  getAppliedTabs,
  getGroupsWithTracking,
  getTabsForWhichCallsNeedToBePerformed,
  getTrackedTabs,
  normalizeFilters }                                                     from '@/components/filter-panel/filter-panel.helpers';
import { chartWithUuid }                              from '@/helpers/variables';
import { getProperChartDataService,
  getProperChartSettingsService,
  getProperCreateConfiguration,
  getProperFilterAllConfigurationService,
  getProperFilterConfigurationService,
  getProperRemoveFilterConfiguration,
  getProperSaveFiltersStylesConfiguration,
  getProperTabNamesDependingOnGivenProperty,
  saveProperChartSettingsService }                                                     from '@/components/chart/helpers/chart.helpers';
import { ObjectWithStringKeys }                       from '@/types';
import { getRouteNameByView, handleClientBasicError } from '@/helpers/helpers';
import { Default }                                    from '@/components/chart/helpers/chart-variables';

const actions: ActionTree<State, RootState> = {
  async getInitialSingleChartData({ commit, rootState }, { chartUuid, view }): Promise<void> {
    try {
      commit('setChartUuid', chartUuid);
      commit('setChartLoader', true);
      const chartFilterPanel: FilterPanelState = rootState.views[ getRouteNameByView(view) ][ chartWithUuid(chartUuid) ].filterPanel;
      const appliedGeneralTab: string = getAppliedTabInGivenGroup(chartFilterPanel.groups, GROUP_NAME.GENERAL);
      const filters: RequestFilter[] = normalizeFilters(chartFilterPanel.selectedFilters);
      commit('filterPanel/setAppliedFiltersOnChart', filters);
      const functions: string[] = chartFilterPanel.selectedFunctions;
      const leadFilter: string = chartFilterPanel.currentLeadFilter.name;
      const { data }: { data: { chartData: ChartDataResponse[] } } = await getProperChartDataService(view, filters, functions, appliedGeneralTab, leadFilter);
      commit('setChartData', data.chartData);
      commit('setChartLoader', false);
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      } else {
        throw new Error(e);
      }
    }
  },
  async getChartDataOnApply({ state, commit, rootState }, payload): Promise<void> {
    const { chartUuid, view, filterOption, removeFilter }: { chartUuid: string; view: string; filterOption?: any; removeFilter?: boolean } = payload;
    const chartFilterPanel: FilterPanelState = rootState.views[ getRouteNameByView(view) ][ chartWithUuid(chartUuid) ].filterPanel;
    const groupsWithTracking: string[] = getGroupsWithTracking(chartFilterPanel.groups);
    const trackedTabs: TrackedTab[] = getTrackedTabs(chartFilterPanel.groups);
    const promiseArray: AxiosPromise[] = [];
    const functions: string[] = chartFilterPanel.selectedFunctions;

    const appliedTabs: string[] = getAppliedTabs(chartFilterPanel.groups);
    commit('filterPanel/setAppliedTabs', appliedTabs);

    groupsWithTracking.forEach((groupWithTrackedTabs: string) => {
      const trackedTabsOfCurrentGroup: TrackedTab[] = trackedTabs.filter((trackedTab: TrackedTab) => trackedTab.group === groupWithTrackedTabs);
      const appliedTabInGivenGroup: string = getAppliedTabInGivenGroup(chartFilterPanel.groups, groupWithTrackedTabs);
      const tabsForWhichCallsNeedToBePerformed: string[] = getTabsForWhichCallsNeedToBePerformed(trackedTabsOfCurrentGroup, appliedTabInGivenGroup);

      tabsForWhichCallsNeedToBePerformed.forEach(async (tab: string) => {
        if (!filterOption) {
          commit('filterPanel/addSelectedFiltersToSelectedCollection', { groupName: groupWithTrackedTabs, tabName: tab, view });
          commit('filterPanel/removeSelectedFiltersFromDisabledFilters');
        } else {
          const isFilterOptionDisabled: boolean = (state.filterPanel as FilterPanelState).disabledFilters.some((disabledFilter: FilterOption) => (
            disabledFilter.uuid === filterOption.uuid && disabledFilter.valueName === filterOption.valueName
          ));

          if (isFilterOptionDisabled || removeFilter) {
            commit('filterPanel/removeSingleOptionFromSelectedFilter', filterOption);
          } else {
            commit('filterPanel/addSingleOptionToSelectedFilter', filterOption);
          }
        }
        const filters: RequestFilter[] = composeFiltersToSend(chartFilterPanel, groupWithTrackedTabs, tab);
        const leadFilter: string = chartFilterPanel.currentLeadFilter.name;
        promiseArray.push(getProperChartDataService(view, filters, functions, tab, leadFilter));
      });
    });

    try {
      commit('setChartLoader', true);
      const responses: AxiosResponse[] = await Promise.all(promiseArray);
      const allChartsData: ChartDataResponse[] = [];
      responses.forEach((singleResponse: AxiosResponse) => {
        allChartsData.push(...singleResponse.data.chartData);
      });
      commit('setChartData', allChartsData);
      commit('setChartLoader', false);
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      }
    }
  },
  async getChartSettings({ commit, rootState }, { view }: { view: string }): Promise<void> {
    try {
      const { data }: { data: ObjectWithStringKeys<boolean> } = await getProperChartSettingsService(view);
      const { logarithmic, tableView, scrollToRight }: ObjectWithStringKeys<boolean> = data;
      commit('setIsLogarithmicAxis', logarithmic);
      commit('setIsTableView', tableView);
      commit('setIsScrollRight', scrollToRight);
      commit('setInitialChartSettings', data);
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      } else {
        throw new Error(e);
      }
    }
  },
  async saveChartSettings({ state, rootState, commit }, { view }: { view: string }): Promise<void> {
    try {
      const dataToSend: ObjectWithStringKeys<boolean> = {
        logarithmic: state.isLogarithmicAxis,
        scrollToRight: state.isScrollRight,
        tableView: state.isTableView,
      };
      const isLogarithmicChanged: boolean = dataToSend.logarithmic !== state.initialIsLogarithmicAxis;
      const isScrollToRightChanged: boolean = dataToSend.scrollToRight !== state.initialIsScrollRight;
      const isTableViewChanged: boolean = dataToSend.tableView !== state.initialIsTableView;

      if (isLogarithmicChanged || isScrollToRightChanged || isTableViewChanged) {
        commit('setInitialChartSettings', dataToSend);
        await saveProperChartSettingsService(view, dataToSend);
      }
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      } else {
        throw new Error(e);
      }
    }
  },
  async getFilterAllConfigurations({ commit, state }, { view }): Promise<void> {
    try {
      const { data }: { data: string[] } = await getProperFilterAllConfigurationService(view);
      const modifyData: FilterConfiguration[] = data.map((configurationName: string) => ({ name: configurationName, json: null }));
      if (!data.includes(Default)) {
        const filterPanelState: FilterPanelState = state.filterPanel as FilterPanelState;
        const functionsGroup: Group = filterPanelState.groups.find((group: Group) => group.groupName === GROUP_NAME.FUNCTION) as Group;
        const functionsArray: string[] = functionsGroup.tabs[ 0 ].content.reduce((acc: string[], curr: Filter) => [ ...acc, curr.name ], []);

        const requestParams: FilterConfigurationJSON = {
          selectedFilters: [],
          selectedFunctions: functionsArray,
          appliedTabs: getProperTabNamesDependingOnGivenProperty(filterPanelState.groups, 'isApplied'),
          trackedTabs: getProperTabNamesDependingOnGivenProperty(filterPanelState.groups, 'isTracked'),
          leadFilter: filterPanelState.currentLeadFilter,
        };

        modifyData.unshift({ name: Default, json: cloneDeep(requestParams) });

        try {
          await getProperCreateConfiguration(view, { name: Default, json: JSON.stringify(requestParams) });
        } catch (e) {
          handleClientBasicError(e);
        }
      }

      commit('setFilterAllConfigurations', modifyData);
    } catch (e) {
      handleClientBasicError(e);
    }
  },
  async getAndSetDefaultFilterConfigurationBeforeInitialRun({ state, commit, dispatch }, { view }): Promise<void> {
    const defaultConfiguration: FilterConfiguration = state.filterConfigurations
      .find((config: FilterConfiguration) => config.name === Default) as FilterConfiguration;

    if (!defaultConfiguration.json) {
      try {
        const { data }: { data: FilterConfiguration } = await getProperFilterConfigurationService(view, Default);
        defaultConfiguration.json = cloneDeep(JSON.parse(data.json as string));
      } catch (e) {
        handleClientBasicError(e);
      }
    }
    commit('setFilterActiveConfiguration', Default);
    dispatch('filterPanel/applyConfiguration', defaultConfiguration.json);
  },
  async getFilterConfiguration({ state, commit, dispatch, rootState }, { view, chartUuid, configurationName }): Promise<void> {
    commit('setFilterActiveConfiguration', configurationName);

    const isConfigurationAlreadyDownloaded: boolean = state.filterConfigurations.some((configuration: FilterConfiguration) => configuration.name === configurationName && configuration.json !== null);
    if (!isConfigurationAlreadyDownloaded) {
      try {
        const { data }: { data: FilterConfiguration } = await getProperFilterConfigurationService(view, configurationName);
        commit('setFilterConfiguration', data);
      } catch (e) {
        if (e.response?.status.toString()
          .startsWith('4')) {
          Notification.error(
            i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
            i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
          );
        } else {
          throw new Error(e);
        }
      }
    }

    const activeConfiguration: FilterConfiguration | undefined = state.filterConfigurations.find((configuration: FilterConfiguration) => configuration.name === configurationName);
    if (activeConfiguration) {
      dispatch('filterPanel/applyConfiguration', activeConfiguration.json);

      if (state.filterPanel) {
        state.filterPanel.groups.forEach((group: Group) => {
          group.tabs.forEach((tab: Tab) => {
            if (tab.isApplied) {
              commit('filterPanel/addSelectedFiltersToSelectedCollection', { groupName: group.groupName, tabName: tab.tabName, view });
            }
          });
        });
        dispatch('getChartDataOnApply', { view, chartUuid });
      }
    }
  },
  async createFilterConfiguration({ state, commit, rootState }, { configurationName, view }): Promise<void> {
    const filterPanelState = state.filterPanel as FilterPanelState;
    const requestParams: FilterConfigurationJSON = {
      selectedFilters: filterPanelState.selectedFilters,
      selectedFunctions: filterPanelState.selectedFunctions,
      appliedTabs: getProperTabNamesDependingOnGivenProperty(filterPanelState.groups, 'isApplied'),
      trackedTabs: getProperTabNamesDependingOnGivenProperty(filterPanelState.groups, 'isTracked'),
      leadFilter: filterPanelState.currentLeadFilter,
    };

    commit('setFilterActiveConfiguration', configurationName);
    commit('addFilterConfiguration', { name: configurationName, json: cloneDeep(requestParams) });

    try {
      await getProperCreateConfiguration(view, { name: configurationName, json: JSON.stringify(requestParams) });
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      } else {
        throw new Error(e);
      }
    }
  },
  async removeFilterConfiguration({ state, commit, rootState }, { configurationName, view }): Promise<void> {
    if (state.filterActiveConfiguration === configurationName) {
      commit('setFilterActiveConfiguration', '');
    }
    commit('removeFilterConfiguration', configurationName);

    try {
      await getProperRemoveFilterConfiguration(view, configurationName);
    } catch (e) {
      if (e.response?.status.toString()
        .startsWith('4')) {
        Notification.error(
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.error`),
          i18n.t(`${rootState.i18n.GLOBAL_PATH}.errors.somethingWentWrong`),
        );
      } else {
        throw new Error(e);
      }
    }
  },
  async updateFilterConfiguration({ dispatch }, { configurationName, view }): Promise<void> {
    await dispatch('removeFilterConfiguration', { configurationName, view });
    await dispatch('createFilterConfiguration', { configurationName, view });
  },
  clearSelectedFilters({ state, commit, dispatch }, { view, chartUuid }): void {
    dispatch('filterPanel/clearAllFilters', true);
    commit('filterPanel/clearDisabledFilters');

    if (state.filterPanel) {
      state.filterPanel.groups.forEach((group: Group) => {
        group.tabs.forEach((tab: Tab) => {
          if (tab.isApplied) {
            commit('filterPanel/addSelectedFiltersToSelectedCollection', { groupName: group.groupName, tabName: tab.tabName, view });
          }
        });
      });
      dispatch('getChartDataOnApply', { view, chartUuid });
    }
  },
  closeSelectedFilterOption({ state, commit, dispatch }, { view, chartUuid, filterOption }): void {
    const isFilterOptionDisabled: boolean = (state.filterPanel as FilterPanelState).disabledFilters.some((disabledFilter: FilterOption) => (
      disabledFilter.uuid === filterOption.uuid && disabledFilter.valueName === filterOption.valueName
    ));

    if (isFilterOptionDisabled) {
      commit('filterPanel/removeDisabledFilter', filterOption);
    } else {
      switch (filterOption.type) {
      case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
        commit('filterPanel/changeFilterOptionValue', {
          uuid: filterOption.uuid,
          checkedOptions: filterOption.values.filter((value: string) => value !== filterOption.valueName),
        });
        break;
      case FILTER_TYPE.MULTI_DATE_RANGE:
        commit('filterPanel/changeSameDateRanges', {
          uuid: filterOption.uuid,
          valueName: filterOption.valueName,
          status: false,
        });
        break;
      default:
        break;
      }
    }

    dispatch('getChartDataOnApply', { view, chartUuid, filterOption, removeFilter: true });
  },
  switchSelectedFilterOption({ state, commit, dispatch }, { view, chartUuid, filterOption }): void {
    commit('filterPanel/switchDisabledFilter', filterOption);

    switch (filterOption.type) {
    case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
      const filterDisabledOptions: string[] = (state.filterPanel as FilterPanelState).disabledFilters.reduce((acc: string[], curr: FilterOption) => {
        if (curr.uuid === filterOption.uuid) {
          return [ ...acc, curr.valueName as string ];
        }
        return [ ...acc ];
      }, []);

      let checkedFilterOptions: string[] = [];
      if (filterDisabledOptions.includes(filterOption.valueName)) {
        checkedFilterOptions = filterOption.values.filter((filterOptionValue: string) => filterOptionValue !== filterOption.valueName);
      } else {
        const sameFilterInSelectedFilters: SelectedFilter | undefined = (state.filterPanel as FilterPanelState).selectedFilters.find((selectedFilter: SelectedFilter) => (
          selectedFilter.uuid === filterOption.uuid
        ));

        checkedFilterOptions = sameFilterInSelectedFilters ? [ ...sameFilterInSelectedFilters.values, filterOption.valueName ] : [ filterOption.valueName ];
      }

      commit('filterPanel/changeFilterOptionValue', {
        uuid: filterOption.uuid,
        checkedOptions: checkedFilterOptions,
      });
      break;
    case FILTER_TYPE.MULTI_DATE_RANGE:
      commit('filterPanel/changeSameDateRanges', {
        uuid: filterOption.uuid,
        valueName: filterOption.valueName,
        status: !(state.filterPanel as FilterPanelState).disabledFilters.some((disabledFilter: FilterOption) => disabledFilter.valueName === filterOption.valueName),
      });
      break;
    default:
      break;
    }

    dispatch('getChartDataOnApply', { view, chartUuid, filterOption });
  },
  async saveFiltersStyleConfig(context, payload: { view: string; filterStyles: FilterStylesObject }): Promise<void> {
    try {
      await getProperSaveFiltersStylesConfiguration(payload.view, payload.filterStyles);
    } catch (e) {
      handleClientBasicError(e);
    }
  },
};

export default actions;
